import { useEffect, useState, useRef } from 'react';
import { getAuth, signOut } from 'firebase/auth';
import { auth, app } from '../firebase';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { getMessaging, getToken, onMessage } from "firebase/messaging";
import axios from 'axios';
import './Dashboard.css';
import {
  FaHome,
  FaUsers,
  FaBell,
  FaUser,
  FaFileAlt,
  FaFolder,
  FaEnvelope,
  FaWallet,
  FaSignOutAlt,
  FaToggleOn,
  FaToggleOff,
  FaCheck,
  FaTimes,
  FaMapMarkerAlt,
  FaVideo,
  FaComments
} from 'react-icons/fa';

function Dashboard() {
  const [loading, setLoading] = useState(true);
  const [availability, setAvailability] = useState(false);
  const [userId, setUserId] = useState(null);
  const [token, setToken] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [dismissedNotifications, setDismissedNotifications] = useState([]);
  const [seenStudentNotificationIds, setSeenStudentNotificationIds] = useState([]);
  const [studentNotifications, setStudentNotifications] = useState([]);
  const [, setActiveNav] = useState(''); //activeNav

  const watchIdRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  
  const isWaiting =
    location.pathname.includes('/dashboard/waiting/') ||
    location.pathname.includes('/dashboard/tracking/') ||
    location.pathname.includes('/dashboard/video/');

  const handleLogout = async () => {
    try {
      const auth = getAuth();
      await signOut(auth);
      navigate('/auth');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  useEffect(() => {
    const registerFCMToken = async () => {
      try {
        const messaging = getMessaging(app);
        const currentToken = await getToken(messaging, { vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY });
        if (currentToken) {
          await axios.post(`${process.env.REACT_APP_API_BASE_URL}/save-fcm-token`, 
            { uid: userId, token: currentToken }, 
            { headers: { Authorization: `Bearer ${token}` } }
          );
        }

        onMessage(messaging, (payload) => {
          console.log('Foreground FCM message:', payload);
          alert(`New session request from ${payload.data.studentName}`);
        });
      } catch (err) {
        console.error('FCM registration error:', err);
      }
    };
  
    if (userId && token) registerFCMToken();
  }, [userId, token]);

  useEffect(() => {
    const fetchData = async () => {
      const auth = getAuth();
      const currentUser = auth.currentUser;

      if (!currentUser) {
        console.error('No user is currently logged in');
        setLoading(false);
        return;
      }

      try {
        const uid = currentUser.uid;
        const idToken = await currentUser.getIdToken();

        setUserId(uid);
        setToken(idToken);

        const [userData, tutorNotifs] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_BASE_URL}/user/${uid}/data`, {
            headers: { 'Authorization': `Bearer ${idToken}` }
          }),
          axios.get(`${process.env.REACT_APP_API_BASE_URL}/tutor/session-requests`, {
            headers: { 'Authorization': `Bearer ${idToken}` }
          })
        ]);

        setAvailability(userData.data.availability);
        setNotifications(tutorNotifs.data);
      } catch (error) {
        console.error('Error fetching data:', error);
        setAvailability(false);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    const intervalId = setInterval(fetchData, 5000);
    return () => clearInterval(intervalId);
  }, []);

  const toggleAvailability = async () => {
    const user = auth.currentUser;
    if (!user) return;

    const uid = user.uid;
    const isCurrentlyAvailable = availability;

    try {
      const idToken = await user.getIdToken();

      if (!isCurrentlyAvailable) {
        try {
          const messaging = getMessaging(app);
          const currentToken = await getToken(messaging, { vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY });

          if (currentToken) {
            await axios.post(
              `${process.env.REACT_APP_API_BASE_URL}/save-fcm-token`,
              { uid, token: currentToken },
              { headers: { Authorization: `Bearer ${idToken}` } }
            );
          }
        } catch (err) {
          console.error("FCM registration failed:", err);
        }

        if (!navigator.geolocation) {
          alert("Geolocation is not supported by your browser.");
          return;
        }

        await axios.put(
          `${process.env.REACT_APP_API_BASE_URL}/user/${uid}/availability`,
          { availability: true },
          { headers: { Authorization: `Bearer ${idToken}` } }
        );
        setAvailability(true);

        watchIdRef.current = navigator.geolocation.watchPosition(
          async (pos) => {
            const { latitude, longitude } = pos.coords;
            try {
              const freshToken = await user.getIdToken(true);
              await axios.put(
                `${process.env.REACT_APP_API_BASE_URL}/users/${uid}/location`,
                { lat: latitude, lng: longitude },
                { headers: { Authorization: `Bearer ${freshToken}` } }
              );
            } catch (err) {
              console.error("Error updating location:", err);
            }
          },
          (err) => console.error("Error getting position:", err),
          { enableHighAccuracy: true, maximumAge: 5000, timeout: 10000 }
        );
      } else {
        if (watchIdRef.current !== null) {
          navigator.geolocation.clearWatch(watchIdRef.current);
          watchIdRef.current = null;
        }

        await axios.put(
          `${process.env.REACT_APP_API_BASE_URL}/user/${uid}/availability`,
          { availability: false },
          { headers: { Authorization: `Bearer ${idToken}` } }
        );
        setAvailability(false);

        await axios.delete(
          `${process.env.REACT_APP_API_BASE_URL}/users/${uid}/location`,
          { headers: { Authorization: `Bearer ${idToken}` } }
        );
      }
    } catch (err) {
      console.error("Error toggling availability:", err);
    }
  };

  useEffect(() => {
    return () => {
      if (watchIdRef.current !== null) navigator.geolocation.clearWatch(watchIdRef.current);
    };
  }, []);

  const handleNotificationResponse = async (requestId, accepted) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/session-requests/${requestId}/respond`,
        { action: accepted ? 'accept' : 'reject' },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.data.success && accepted) {
        const { sessionId, startsAt, type, studentId, tutorId } = response.data;

        if (!sessionId) {
          console.error("No sessionId received! Backend error likely.");
          return;
        }

        if (type === 'face-to-face') {
          const role = userId === tutorId ? 'tutor' : 'student';
          navigate(`/dashboard/tracking/${sessionId}`, {
            state: { role, studentId, tutorId }
          });
        } else {
          const startTime = typeof startsAt === 'object' && startsAt.toMillis
            ? startsAt.toMillis()
            : startsAt;

          const waitTime = startTime - Date.now();
          window.location.href = `/dashboard/waiting/${sessionId}`;
          setTimeout(() => {
            navigate(`/dashboard/video/${sessionId}`);
          }, waitTime);
        }
      }

      setNotifications(prev => prev.filter(n => n.id !== requestId));
    } catch (error) {
      console.error('Error responding to notification:', error);
      if (error.response?.data?.error) {
        alert(error.response.data.error);
      }
    }
  };

  useEffect(() => {
    if (!userId || !token) return;

    const checkStudentNotifications = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/student/session-requests`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        setStudentNotifications(response.data);
      } catch (error) {
        console.error('Error fetching student notifications:', error);
      }
    };

    checkStudentNotifications();
    const intervalId = setInterval(checkStudentNotifications, 5000);
    return () => clearInterval(intervalId);
  }, [userId, token]);

  useEffect(() => {
    const newNotifs = studentNotifications.filter(n => {
      const updatedAt = n.updatedAt?.toMillis?.() || n.updatedAt;
      return !seenStudentNotificationIds.includes(n.id) && Date.now() - updatedAt < 5000;
    });

    newNotifs.forEach(n => {
      setSeenStudentNotificationIds(prev => [...prev, n.id]);
      setTimeout(() => setDismissedNotifications(prev => [...prev, n.id]), 7000);
    });
  }, [studentNotifications, seenStudentNotificationIds]);

  const processedStudentNotifications = studentNotifications.map(n => ({
    ...n,
    updatedAt: typeof n.updatedAt === 'object' && n.updatedAt.toMillis
      ? n.updatedAt.toMillis()
      : n.updatedAt
  }));

  return (
    <div className="dashboard">
      {/* Top Navigation Bar */}
      <div className="top-nav">
        <div className="nav-header">
          <h3>TeachMeGo</h3>
        </div>
        
        <nav className="nav-menu">
          <Link
            to="/dashboard"
            className={`nav-item ${location.pathname === '/dashboard' ? 'active' : ''} ${isWaiting ? 'disabled-link' : ''}`}
            onClick={(e) => {
              if (isWaiting) e.preventDefault();
              setActiveNav('home');
            }}
            title="Home"
          >
            <FaHome className="nav-icon" />
            <span className="nav-text">Home</span>
          </Link>

          <Link
            to="/dashboard/notifications"
            className={`nav-item ${location.pathname.includes('/notifications') ? 'active' : ''} ${isWaiting ? 'disabled-link' : ''}`}
            onClick={(e) => {
              if (isWaiting) e.preventDefault();
              setActiveNav('notifications');
            }}
            title="Notifications"
          >
            <FaBell className="nav-icon" />
            <span className="nav-text">Notifications</span>
          </Link>

          <Link
            to="/dashboard/profile"
            className={`nav-item ${location.pathname.includes('/profile') ? 'active' : ''} ${isWaiting ? 'disabled-link' : ''}`}
            onClick={(e) => {
              if (isWaiting) e.preventDefault();
              setActiveNav('profile');
            }}
            title="Profile"
          >
            <FaUser className="nav-icon" />
            <span className="nav-text">Profile</span>
          </Link>

          <Link
            to="/dashboard/apply"
            className={`nav-item ${location.pathname.includes('/apply') ? 'active' : ''} ${isWaiting ? 'disabled-link' : ''}`}
            onClick={(e) => {
              if (isWaiting) e.preventDefault();
              setActiveNav('apply');
            }}
            title="Apply to Tutor"
          >
            <FaFileAlt className="nav-icon" />
            <span className="nav-text">Apply</span>
          </Link>

          <Link
            to="/dashboard/applications"
            className={`nav-item ${location.pathname.includes('/applications') ? 'active' : ''} ${isWaiting ? 'disabled-link' : ''}`}
            onClick={(e) => {
              if (isWaiting) e.preventDefault();
              setActiveNav('applications');
            }}
            title="My Applications"
          >
            <FaFolder className="nav-icon" />
            <span className="nav-text">Applications</span>
          </Link>

          <Link
            to="/dashboard/request"
            className={`nav-item ${location.pathname.includes('/request') ? 'active' : ''} ${isWaiting ? 'disabled-link' : ''}`}
            onClick={(e) => {
              if (isWaiting) e.preventDefault();
              setActiveNav('request');
            }}
            title="Request a Session"
          >
            <FaEnvelope className="nav-icon" />
            <span className="nav-text">Request</span>
          </Link>

          <Link
            to="/dashboard/wallet"
            className={`nav-item ${location.pathname.includes('/wallet') ? 'active' : ''} ${isWaiting ? 'disabled-link' : ''}`}
            onClick={(e) => {
              if (isWaiting) e.preventDefault();
              setActiveNav('wallet');
            }}
            title="Wallet"
          >
            <FaWallet className="nav-icon" />
            <span className="nav-text">Wallet</span>
          </Link>

          <Link
            to="/dashboard/connect"
            className={`nav-item ${location.pathname.includes('/connect') ? 'active' : ''} ${isWaiting ? 'disabled-link' : ''}`}
            onClick={(e) => {
              if (isWaiting) e.preventDefault();
              setActiveNav('connect');
            }}
            title="Connect"
          >
            <FaUsers className="nav-icon" />
            <span className="nav-text">Connect</span>
          </Link>


          <Link
          to="/dashboard/chats"
          className={`nav-item ${location.pathname.includes('/chats') ? 'active' : ''} ${isWaiting ? 'disabled-link' : ''}`}
          onClick={(e) => {
            if (isWaiting) e.preventDefault();
            setActiveNav('chats');
          }}
          title="Chats"
          >
          <FaComments className="nav-icon" />
          <span className="nav-text">Chats</span>
          </Link>


              
        </nav>

        <div className="nav-controls">
          <div className="availability-toggle-wrapper">
            {loading ? (
              <div className="loading-availability">Loading...</div>
            ) : (
              <button
                className={`availability-toggle-btn ${availability ? 'active' : ''}`}
                onClick={isWaiting ? undefined : toggleAvailability}
                disabled={isWaiting}
                title={availability ? "Currently Available" : "Set as Available"}
              >
                {availability ? <FaToggleOn className="toggle-icon" /> : <FaToggleOff className="toggle-icon" />}
                <span className="toggle-text">
                  {availability ? 'Available' : 'Unavailable'}
                </span>
              </button>
            )}
          </div>

          <button
            className="logout-btn"
            onClick={handleLogout}
            disabled={isWaiting}
            title="Logout"
          >
            <FaSignOutAlt className="logout-icon" />
            <span className="logout-text">Logout</span>
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="main-content">
        {isWaiting && (
          <div className="waiting-overlay">
            <p>You are in a session. Please wait to be redirected or complete your session.</p>
          </div>
        )}

        {/* Student Notifications */}
        {processedStudentNotifications.length > 0 && (
          <div className="student-notifications">
            {processedStudentNotifications
              .filter(n =>
                !dismissedNotifications.includes(n.id) &&
                seenStudentNotificationIds.includes(n.id)
              )
              .map(n => (
                <div key={n.id} className={`notification-card ${n.status}`}>
                  <div className="notification-icon">
                    {n.type === 'face-to-face' ? <FaMapMarkerAlt /> : <FaVideo />}
                  </div>
                  <div className="notification-content">
                    <p className="notification-message">
                      {n.status === 'accepted'
                        ? `${n.tutorName} accepted your ${n.type} session request for ${n.courseName}`
                        : `${n.tutorName} declined your ${n.type} session request for ${n.courseName}`}
                    </p>
                    <span className="notification-course">{n.courseName}</span>
                  </div>
                </div>
              ))}
          </div>
        )}

        {/* Tutor Session Request Notifications */}
        <div className="notifications-container">
          {notifications.map(notification => (
            <div key={notification.id} className="notification-card session-request">
              <div className="notification-header">
                <div className="notification-icon">
                  {notification.type === 'face-to-face' ? <FaMapMarkerAlt /> : <FaVideo />}
                </div>
                <div className="notification-info">
                  <h4>New Session Request</h4>
                  <p className="student-name">{notification.studentName}</p>
                </div>
              </div>
              
              <div className="notification-details">
                <div className="detail-item">
                  <span className="detail-label">Course:</span>
                  <span className="detail-value">{notification.courseName}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Type:</span>
                  <span className="detail-value">{notification.type}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Duration:</span>
                  <span className="detail-value">{notification.duration} minutes</span>
                </div>
              </div>

              <div className="notification-actions">
                <button 
                  className="accept-btn"
                  onClick={() => handleNotificationResponse(notification.id, true)} 
                  disabled={isWaiting}
                  title="Accept Request"
                >
                  <FaCheck className="action-icon" />
                  <span>Accept</span>
                </button>
                <button 
                  className="decline-btn"
                  onClick={() => handleNotificationResponse(notification.id, false)} 
                  disabled={isWaiting}
                  title="Decline Request"
                >
                  <FaTimes className="action-icon" />
                  <span>Decline</span>
                </button>
              </div>

              <div className="notification-timer">
                <div
                  className="timer-bar"
                  style={{
                    width: `${Math.max(0, 100 - ((Date.now() - notification.createdAt) / 300))}%`
                  }}
                />
              </div>
            </div>
          ))}
        </div>

        <Outlet />
      </div>
    </div>
  );
}

export default Dashboard;
